'use client'

import { useEffect } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
    $getSelection,
    $isRangeSelection,
    $isNodeSelection,
    KEY_BACKSPACE_COMMAND,
    KEY_DELETE_COMMAND,
    COMMAND_PRIORITY_HIGH,
} from 'lexical'
import { $isMentionNode } from './MentionNode'

function $getAdjacentMentionNode(selection, direction) {
    if ($isNodeSelection(selection)) {
        const nodes = selection.getNodes()
        const mentionNode = nodes.find($isMentionNode)
        if (mentionNode) return mentionNode
    }

    if (!$isRangeSelection(selection) || !selection.isCollapsed()) return null

    const anchor = selection.anchor
    const anchorNode = anchor.getNode()

    if ($isMentionNode(anchorNode)) return anchorNode

    if (anchor.type === 'element') {
        const element = anchorNode
        const childAtOffset = direction === 'backward'
            ? element.getChildAtIndex(anchor.offset - 1)
            : element.getChildAtIndex(anchor.offset)
        if (childAtOffset && $isMentionNode(childAtOffset)) return childAtOffset
    }

    if (anchor.type === 'text') {
        if (direction === 'backward' && anchor.offset === 0) {
            const prev = anchorNode.getPreviousSibling()
            if (prev && $isMentionNode(prev)) return prev
        }
        if (direction === 'forward' && anchor.offset === anchorNode.getTextContentSize()) {
            const next = anchorNode.getNextSibling()
            if (next && $isMentionNode(next)) return next
        }
    }

    return null
}

export default function MentionDeletionPlugin() {
    const [editor] = useLexicalComposerContext()

    useEffect(() => {
        const removeBackspace = editor.registerCommand(
            KEY_BACKSPACE_COMMAND,
            (event) => {
                const selection = $getSelection()
                if (!selection) return false

                const mention = $getAdjacentMentionNode(selection, 'backward')
                if (mention) {
                    event.preventDefault()
                    mention.remove()
                    return true
                }

                return false
            },
            COMMAND_PRIORITY_HIGH
        )

        const removeDelete = editor.registerCommand(
            KEY_DELETE_COMMAND,
            (event) => {
                const selection = $getSelection()
                if (!selection) return false

                const mention = $getAdjacentMentionNode(selection, 'forward')
                if (mention) {
                    event.preventDefault()
                    mention.remove()
                    return true
                }

                return false
            },
            COMMAND_PRIORITY_HIGH
        )

        return () => {
            removeBackspace()
            removeDelete()
        }
    }, [editor])

    return null
}
